import React from 'react';
import styled, { ThemeProvider } from 'styled-components';
import {
  black,
  white,
  gray90,
  gray95,
  screenSmMax,
  spacer8,
  spacer16,
  spacer20,
  spacer40,
  spacer24,
  body1FontSize,
} from '@spotify-internal/encore-foundation';
import { Type, FormSelect } from '@spotify-internal/encore-web';
import { lightTheme } from '../Theme';
import { SearchHit } from '../Search';
import { ContentItem } from './ContentItem';
import { Filter } from './filterType';

const ListWrapper = styled.div`
  padding: 0px;
`;

const ContentWrapper = styled.div`
  padding: 0px;
  width: 100%;

  :nth-child(odd) {
    background: ${gray90};
  }

  :nth-child(even) {
    background: ${gray95};
  }
`;

const FilterBarWrapper = styled.div<{
  filterBarColor?: string;
}>`
  width: 100%;
  background: ${({ filterBarColor }) => filterBarColor};
  padding: ${spacer20} ${spacer40};
  display: flex;
  flex-direction: row;
  justify-content: space-between;

  @media (max-width: ${screenSmMax}) {
    flex-direction: column;
    padding: ${spacer16};
  }

  .SelectContainer {
    width: 30%;
  }
`;

const Headline = styled(Type.h1).attrs({
  variant: Type.heading2,
})<{
  filterBarColor?: string;
}>`
  color: ${({ filterBarColor }) => (filterBarColor === gray90 ? black : white)};
  padding-top: ${spacer24};

  @media (max-width: ${screenSmMax}) {
    padding: 0px;
    width: 100%;
  }
`;

const FilterWrapper = styled.div<{
  filterBarColor?: string;
}>`
  color: ${({ filterBarColor }) => (filterBarColor === gray90 ? black : white)};

  @media (max-width: ${screenSmMax}) {
    flex-direction: column;
    padding-top: ${spacer8};
    width: 70%;
  }
`;

const Filters = styled.div`
  display: flex;
  flex-direction: row;
`;

const DropDownBox = styled.div`
  padding-right: ${spacer20};
  select,
  input {
    font-size: ${body1FontSize};
  }

  &:last-child {
    padding: 0px;
  }
`;

const FilterHeader = styled(Type.p).attrs({
  variant: Type.cta3,
})``;

type ContentListType = {
  filterBarTitle: string;
  items?: SearchHit[];
  filters?: Filter[];
  filterBarColor?: string;
  showFilters?: boolean;
  emptyMessage?: string;
};

export const ContentList: React.FC<ContentListType> = ({
  items = [],
  filterBarTitle = '',
  filters,
  filterBarColor = black,
  showFilters = true,
  emptyMessage = 'There are no items matching the specified filters.',
}) => {
  const [itemList, setItemList] = React.useState<SearchHit[]>(items);

  function filterDropdown(formValue: string, filterValue: string) {
    // @ts-ignore TODO - how to fix this linting error?
    if (formValue !== 'all') {
      const filteredItems = items.filter(function dropdown(item) {
        // @ts-ignore TODO - how to fix this linting error?
        return item[filterValue] === formValue;
      });
      setItemList(filteredItems);
    } else {
      setItemList(items);
    }
  }

  return (
    <ThemeProvider theme={lightTheme}>
      <ListWrapper>
        <FilterBarWrapper filterBarColor={filterBarColor}>
          <Headline filterBarColor={filterBarColor}>{filterBarTitle}</Headline>
          {filters && showFilters && (
            <FilterWrapper filterBarColor={filterBarColor}>
              <FilterHeader>Show</FilterHeader>
              <Filters>
                {filters.map((filter, key) => (
                  <DropDownBox key={key}>
                    <FormSelect
                      defaultValue="all"
                      onChange={e => {
                        // @ts-ignore TODO How to remove this linting error?
                        filterDropdown(e.target.value, filter.filterValue);
                      }}
                      data-testid={`dropdown-${filter.filterValue}`}
                    >
                      <option value="all">
                        {filter.allLabel ||
                          filter.filterValue.charAt(0).toUpperCase() +
                            filter.filterValue.slice(1)}
                      </option>
                      {filter.filterOptions.map((filterOption, index) => (
                        <option
                          key={index}
                          value={filterOption}
                          data-testid={`option-${filterOption}`}
                        >
                          {filterOption}
                        </option>
                      ))}
                    </FormSelect>
                  </DropDownBox>
                ))}
              </Filters>
            </FilterWrapper>
          )}
        </FilterBarWrapper>

        {itemList.length === 0 && (
          <ContentWrapper>
            <ContentItem>{emptyMessage}</ContentItem>
          </ContentWrapper>
        )}

        {itemList.map((item, index) => (
          <ContentWrapper key={index}>
            <ContentItem {...item} />
          </ContentWrapper>
        ))}
      </ListWrapper>
    </ThemeProvider>
  );
};
